import { embedSnippets, type EmbedSubject } from '@/share/embedSnippet'
import { SnippetTabs } from './SnippetTabs'

/**
 * Put this site in somebody else's page, from inside the share sheet.
 *
 * A post is worth one day; a badge in a README is worth every outage after it. That is the whole
 * argument for this panel existing inside the share sheet rather than being buried on the About
 * page — the moment a visitor is most willing to spend thirty seconds wiring a status strip into
 * their team dashboard is the moment they have just watched a wall come down.
 *
 * Three snippets because three hosts behave differently (`share/embedSnippet.ts` has the detail):
 * the iframe is live, and the two badge forms exist because GitHub and npm strip frames out of
 * rendered Markdown, so an image is the only thing that survives in a README.
 *
 * Since F33 this is one of two places those snippets appear — the other is the block at the foot of
 * every status page (`ui/status/ReadmeBlock.tsx`), which is where somebody who came for an outage
 * actually is. The tabs, the panel and the keyboard contract are `SnippetTabs`, shared by both; what
 * is left here is the sheet's own framing and its status line.
 */
export function EmbedBadge({
  subject,
  onStatus,
}: {
  subject: EmbedSubject
  onStatus: (status: { tone: 'ok' | 'warn'; message: string } | null) => void
}) {
  const origin = typeof location === 'undefined' ? '' : location.origin

  return (
    <section className="mha-embed" aria-label="Embed this on your own page">
      <SnippetTabs
        idPrefix="mha-embed"
        snippets={embedSnippets(subject, origin)}
        initial="iframe"
        onCopied={(snippet, copied) =>
          onStatus(
            copied
              ? { tone: 'ok', message: `${snippet.label} snippet copied.` }
              : { tone: 'warn', message: 'Could not reach the clipboard — select the code.' },
          )
        }
      />
    </section>
  )
}
